import {updateIssueBodyMutation as updateIssueBodyQueryId} from '@github-ui/server-persisted-queries'
import type {
  UpdateIssueBodyMutationMutation,
  UpdateIssueBodyMutationMutationVariables,
} from '@github-ui/server-persisted-queries/types/updateIssueBodyMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'

type GeneratedInput = UpdateIssueBodyMutationMutationVariables['input']

export type UpdateIssueBodyInput = {
  readonly id: Extract<GeneratedInput['id'], string>
  readonly body: NonNullable<GeneratedInput['body']>
  readonly bodyVersion: NonNullable<GeneratedInput['bodyVersion']>
}

export type UpdateIssueBodyIssue = NonNullable<NonNullable<UpdateIssueBodyMutationMutation['updateIssue']>['issue']>

type UpdateIssueBodyEdit = NonNullable<UpdateIssueBodyIssue['lastUserContentEdit']>
type UpdateIssueBodyEditor = NonNullable<UpdateIssueBodyEdit['editor']>

type GraphQLError = {readonly message?: unknown; readonly type?: unknown}
type UpdateIssueBodyEnvelope = {
  readonly data?: UpdateIssueBodyMutationMutation | null
  readonly errors?: unknown
}

const EDITOR_TYPENAMES = ['Bot', 'EnterpriseUserAccount', 'Mannequin', 'Organization', 'User'] as const

export class UpdateIssueBodyGraphQLError extends Error {
  readonly type: string | undefined

  constructor(message: string, type?: string) {
    super(message)
    this.name = 'UpdateIssueBodyGraphQLError'
    this.type = type
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function firstGraphQLError(errors: unknown): UpdateIssueBodyGraphQLError | undefined {
  if (!Array.isArray(errors)) return undefined
  for (const entry of errors) {
    const {message, type} = (entry ?? {}) as GraphQLError
    if (typeof message === 'string') {
      return new UpdateIssueBodyGraphQLError(message, typeof type === 'string' ? type : undefined)
    }
  }
  return undefined
}

function isEditorTypename(value: unknown): value is UpdateIssueBodyEditor['__typename'] {
  return typeof value === 'string' && (EDITOR_TYPENAMES as readonly string[]).includes(value)
}

function narrowEditor(value: unknown): UpdateIssueBodyEdit['editor'] {
  if (value === null) return null
  if (!isRecord(value)) throw new Error('Issue body update returned a malformed issue')

  const {__typename, id, url, login} = value
  if (!isEditorTypename(__typename) || typeof id !== 'string' || typeof url !== 'string' || typeof login !== 'string') {
    throw new Error('Issue body update returned a malformed issue')
  }
  return {__typename, id, url, login}
}

function narrowLastUserContentEdit(value: unknown): UpdateIssueBodyIssue['lastUserContentEdit'] {
  if (value === null) return null
  if (!isRecord(value)) throw new Error('Issue body update returned a malformed issue')

  const {id} = value
  if (typeof id !== 'string') throw new Error('Issue body update returned a malformed issue')
  return {id, editor: narrowEditor(value.editor)}
}

function narrowIssue(value: unknown): UpdateIssueBodyIssue {
  if (!isRecord(value)) throw new Error('Issue body update returned a malformed issue')

  const {id, body, bodyHTML, bodyVersion, lastEditedAt, viewerCanReadUserContentEdits} = value
  if (
    typeof id !== 'string' ||
    typeof body !== 'string' ||
    typeof bodyHTML !== 'string' ||
    typeof bodyVersion !== 'string' ||
    (lastEditedAt !== null && typeof lastEditedAt !== 'string') ||
    typeof viewerCanReadUserContentEdits !== 'boolean'
  ) {
    throw new Error('Issue body update returned a malformed issue')
  }

  return {
    id,
    body,
    bodyHTML,
    bodyVersion,
    lastEditedAt,
    viewerCanReadUserContentEdits,
    lastUserContentEdit: narrowLastUserContentEdit(value.lastUserContentEdit),
  }
}

export async function updateIssueBody({
  owner,
  repo,
  issueNumber,
  input,
}: {
  owner: string
  repo: string
  issueNumber: number
  input: UpdateIssueBodyInput
}): Promise<UpdateIssueBodyIssue> {
  const response = await reactFetchJSON(
    `/${owner}/${repo}/issues/${issueNumber}/body?queryId=${encodeURIComponent(updateIssueBodyQueryId)}`,
    {
      method: 'PATCH',
      body: {input},
    },
  )

  if (!response.ok) throw new Error(`Issue body update failed (${response.status})`)

  let json: unknown
  try {
    json = await response.json()
  } catch {
    throw new Error('Issue body update did not return valid JSON')
  }

  if (!isRecord(json)) throw new Error('Issue body update returned an invalid response envelope')

  const envelope = json as UpdateIssueBodyEnvelope
  const issue = envelope.data?.updateIssue?.issue
  // Relay surfaced usable partial responses, so preserve that behavior: when a usable issue is
  // present, top-level GraphQL errors must never override it. A truthy-but-malformed issue is a
  // contract violation and fails here, before any TanStack/Relay cache write.
  if (issue != null) return narrowIssue(issue)

  const graphQLError = firstGraphQLError(envelope.errors)
  if (graphQLError) throw graphQLError
  if (envelope.data == null) throw new Error('Issue body update returned no data')
  throw new Error('Issue body update returned no issue')
}
